import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";

const empty = { name: "", type: "doppia", capacity: 2, price_per_night: 80, description: "" };
const TYPES = ["singola", "doppia", "matrimoniale", "tripla", "suite", "familiare"];

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => api.get("/rooms").then(r => setRooms(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, capacity: Number(form.capacity), price_per_night: Number(form.price_per_night) };
      if (editingId) {
        await api.put(`/rooms/${editingId}`, payload);
        toast.success("Camera aggiornata");
      } else {
        await api.post("/rooms", payload);
        toast.success("Camera creata");
      }
      setForm(empty); setEditingId(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onEdit = (r) => { setForm({ name: r.name, type: r.type, capacity: r.capacity, price_per_night: r.price_per_night, description: r.description || "" }); setEditingId(r.id); };
  const onDelete = async (id) => {
    if (!confirm("Eliminare la camera?")) return;
    await api.delete(`/rooms/${id}`); toast.success("Eliminata"); load();
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">CAMERE</p>
      <h1 className="font-heading font-semibold text-4xl tracking-tight mb-8">Le tue camere</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={onSubmit} className="border border-slate-200 bg-white p-6" data-testid="room-form">
          <div className="text-xs tracked text-slate-500 mb-4">{editingId ? "MODIFICA" : "NUOVA CAMERA"}</div>
          <label className="text-xs text-slate-500">Nome / Numero</label>
          <input data-testid="room-name" required value={form.name} onChange={set("name")}
            className="w-full border border-slate-300 px-3 py-2 mt-1 mb-3 text-sm focus:outline-none focus:border-blue-700" />

          <label className="text-xs text-slate-500">Tipologia</label>
          <select value={form.type} onChange={set("type")}
            className="w-full border border-slate-300 px-3 py-2 mt-1 mb-3 text-sm bg-white focus:outline-none focus:border-blue-700">
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label className="text-xs text-slate-500">Capacità</label>
          <input data-testid="room-capacity" type="number" min={1} required value={form.capacity} onChange={set("capacity")}
            className="w-full border border-slate-300 px-3 py-2 mt-1 mb-3 text-sm focus:outline-none focus:border-blue-700" />

          <label className="text-xs text-slate-500">Prezzo / notte (€)</label>
          <input data-testid="room-price" type="number" step="0.01" required value={form.price_per_night} onChange={set("price_per_night")}
            className="w-full border border-slate-300 px-3 py-2 mt-1 mb-3 text-sm focus:outline-none focus:border-blue-700" />

          <label className="text-xs text-slate-500">Descrizione</label>
          <textarea value={form.description} onChange={set("description")} rows={3}
            className="w-full border border-slate-300 px-3 py-2 mt-1 mb-4 text-sm focus:outline-none focus:border-blue-700" />

          <button data-testid="room-submit" className="w-full bg-stone-900 text-stone-50 px-4 py-2.5 rounded-lg hover:bg-stone-800 transition-colors text-sm">
            <Plus className="inline w-4 h-4 mr-1" /> {editingId ? "Salva" : "Aggiungi"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setForm(empty); setEditingId(null); }}
              className="w-full mt-2 text-slate-600 px-4 py-2 text-sm hover:text-slate-900">Annulla</button>
          )}
        </form>

        <div className="lg:col-span-2 border border-slate-200 bg-white">
          <div className="p-6 border-b border-slate-200 text-xs tracked text-slate-500">{rooms.length} CAMERE</div>
          {rooms.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">Nessuna camera. Aggiungine una.</div>
          ) : (
            <table className="w-full text-sm" data-testid="rooms-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-4">Nome</th>
                  <th className="text-left p-4">Tipo</th>
                  <th className="text-left p-4">Cap.</th>
                  <th className="text-left p-4">€/notte</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {rooms.map(r => (
                  <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="p-4 font-medium">{r.name}</td>
                    <td className="p-4 text-slate-600">{r.type}</td>
                    <td className="p-4 text-slate-600">{r.capacity}</td>
                    <td className="p-4 text-slate-600">€ {r.price_per_night?.toFixed(2)}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => onEdit(r)} className="text-slate-500 hover:text-blue-700 mr-3" data-testid={`edit-${r.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => onDelete(r.id)} className="text-slate-500 hover:text-red-700" data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
